import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { theme } from '~/theme';

import AddIcon from '@mui/icons-material/Add';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridEditInputCell,
  GridExpandMoreIcon,
  GridRowClassNameParams,
  GridRowModes,
  GridRowModesModel,
  useGridApiContext,
} from '@mui/x-data-grid';

import { definitions, operations } from '@luxuryescapes/contract-svc-promo';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import useQuery from '~/hooks/useQuery';

import {
  InternalPromoWithDiscounts,
  PromoCondition,
  PromoConditionRaw,
  PromoConditionTypeConfig,
  createOrUpdatePromoCondition,
  deletePromoCodeCondition,
} from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { noop } from '~/utils/noop';

import PageSubheader from '../Common/Elements/PageSubheader';
import HeaderInfo from '../Common/HeaderInfo';
import Spinner from '../Common/Spinner';
import DebugModal from '../DebugModal/DebugModal';
import MultiDiscountSelect, { renderMultiDiscountValue } from '../PromoV3/MultiDiscountSelect';

import PromoCodeUser from './formatters/PromoCodeUser';

// Helper function to validate and reset condition_apply_type based on condition_type config
function getValidatedConditionApplyType(
  conditionType: string | undefined,
  currentApplyType: string | undefined,
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>,
  alwaysReset = false,
): string | undefined {
  if (!conditionType) {
    return undefined;
  }

  const config = promoCodeConditionTypeConfig.find((ct) => ct.key === conditionType);
  if (!config) {
    return undefined;
  }

  // If the condition type doesn't support condition apply types, clear it
  if (!config.conditionApplyTypes || config.conditionApplyTypes.length === 0) {
    return undefined;
  }

  // If alwaysReset is true (e.g., when condition_type changes), always reset to first option
  if (alwaysReset) {
    return config.conditionApplyTypes[0];
  }

  // Otherwise, validate: if current value is not valid, reset to first option
  if (!currentApplyType || !config.conditionApplyTypes.includes(currentApplyType)) {
    return config.conditionApplyTypes[0];
  }

  // If it's valid, keep it as is
  return currentApplyType;
}

// Custom component for condition type selection that also resets condition_apply_type
function ConditionTypeSelect({
  params,
  promoCodeConditionTypeConfig,
  onConditionTypeChange,
}: {
  params: any;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  onConditionTypeChange: (rowId: string, newConditionType: string) => void;
}) {
  const apiRef = useGridApiContext();
  const [currentValue, setCurrentValue] = useState(params.row.condition_type || '');

  useEffect(() => {
    setCurrentValue(params.row.condition_type || '');
  }, [params.row.condition_type]);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setCurrentValue(newValue);
    // Update the cell value
    apiRef.current.setEditCellValue({ id: params.id, field: 'condition_type', value: newValue });

    // Get the validated condition_apply_type using the helper (always reset when condition_type changes)
    const newApplyType = getValidatedConditionApplyType(
      newValue,
      params.row.condition_apply_type,
      promoCodeConditionTypeConfig,
      true, // alwaysReset = true when condition_type changes
    );

    // Update the condition_apply_type cell value directly BEFORE updating the config
    // This ensures the DataGrid has the correct value
    apiRef.current.setEditCellValue({ id: params.id, field: 'condition_apply_type', value: newApplyType });

    // Update config and reset condition_apply_type in state
    // This will also update the condition_apply_type if needed
    onConditionTypeChange(params.row.id, newValue);
  };

  const valueOptions =
    promoCodeConditionTypeConfig
      .map((ct) => {
        return {
          value: ct.key,
          label: `${ct.isBeingDeprecated ? '~' : ''}${ct.name}${ct.isBeingDeprecated ? '~' : ''}`,
          isBeingDeprecated: ct.isBeingDeprecated,
        };
      })
      .sort((a, b) => {
        if (a.isBeingDeprecated && !b.isBeingDeprecated) {
          return 1;
        } else if (!a.isBeingDeprecated && b.isBeingDeprecated) {
          return -1;
        }
        return 0;
      }) ?? [];

  const config = params.row.config || promoCodeConditionTypeConfig.find((ct) => ct.key === currentValue);

  return (
    <Stack spacing={1} width="100%">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Select value={currentValue} onChange={handleChange} fullWidth size="small">
          {valueOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {config?.longDescription && (
        <Box px={1}>
          <Typography variant="body2">
            {config.longDescription}{' '}
            {!config.isAvailableInAPP && (
              <Typography variant="caption" component="span" color="warning.main">
                [Behavior not yet tested via APP]
              </Typography>
            )}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// Custom component for condition apply type selection
function ConditionApplyTypeSelect({
  value,
  options,
  id,
  field,
}: {
  value: string;
  options: string[];
  id: string;
  field: string;
}) {
  const apiRef = useGridApiContext();
  const [currentValue, setCurrentValue] = useState(value || '');

  // Update local state when the prop value or options change
  useEffect(() => {
    const newValue = value || '';
    // If the current value is not in the new options, reset to first option or empty
    if (newValue && options.length > 0 && !options.includes(newValue)) {
      const resetValue = options[0] || '';
      setCurrentValue(resetValue);
      apiRef.current.setEditCellValue({ id, field, value: resetValue });
    } else {
      setCurrentValue(newValue);
    }
  }, [value, options, id, field, apiRef]);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setCurrentValue(newValue);
    // Only update the cell value, don't stop edit mode
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <Select value={currentValue} onChange={handleChange} fullWidth size="small">
      {options.map((type) => (
        <MenuItem key={type} value={type}>
          {type}
        </MenuItem>
      ))}
    </Select>
  );
}

interface Props {
  promoCode: InternalPromoWithDiscounts;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  onConditionRowSelect: (condition: PromoCondition | null) => void;
  highlightedConditionIds?: Array<string>;
  onConditionUpdate?: () => void;
}

const addUniqueID = (
  condition: PromoConditionRaw,
  promoConditionTypes: Array<PromoConditionTypeConfig>,
): PromoCondition => {
  const existing = 'id_promo_code_condition' in condition;
  return {
    ...condition,
    id: condition.id_promo_code_condition ?? uuid(),
    isNew: !existing,
    config: promoConditionTypes.find((ct) => ct.key === condition.condition_type),
  };
};

function PromoConditions({
  promoCode,
  promoCodeConditionTypeConfig,
  onConditionRowSelect,
  highlightedConditionIds,
  onConditionUpdate,
}: Props) {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const query = useQuery();
  const isDev = !!query.get('dev');

  const [conditions, setConditions] = useState<Array<PromoCondition>>(
    promoCode.promo_conditions.map((condition) => addUniqueID(condition, promoCodeConditionTypeConfig)) ?? [],
  );

  const getRowClassName = (params: GridRowClassNameParams<PromoCondition>): string => {
    if (highlightedConditionIds?.includes(params.row.id_promo_code_condition ?? params.row.id)) {
      return 'highlighted-row';
    }
    return '';
  };

  const handleAddCondition = () => {
    const newCondition = addUniqueID(
      {
        id_promo_code: promoCode?.id_promo_code,
        condition_type: promoCodeConditionTypeConfig[0]?.key,
        isNew: true,
        brand: brand,
      } as unknown as PromoConditionRaw,
      promoCodeConditionTypeConfig,
    );
    setConditions([...conditions, newCondition]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newCondition.id]: { mode: GridRowModes.Edit, fieldToFocus: 'region' },
    }));
  };

  const handleCancelCondition = useCallback(
    (params: PromoCondition) => {
      if (params?.isNew) {
        setConditions(
          conditions.filter(
            (row) => row.id !== params.id && row.id_promo_code_condition !== params.id_promo_code_condition,
          ),
        );
      } else {
        setRowModesModel({
          ...rowModesModel,
          [params.id_promo_code_condition]: { mode: GridRowModes.View, ignoreModifications: true },
        });
      }
      onConditionRowSelect(null);
    },
    [onConditionRowSelect, conditions, rowModesModel],
  );

  const handleEditCondition = useCallback(
    (params: PromoCondition) => {
      setRowModesModel({
        ...rowModesModel,
        [params?.id ?? params?.id_promo_code_condition]: { mode: GridRowModes.Edit, fieldToFocus: 'region' },
      });
      onConditionRowSelect(params);
    },
    [promoCodeConditionTypeConfig, rowModesModel],
  );

  const handleDeleteCondition = async (params: PromoCondition) => {
    if (confirm('are you sure you want to delete this condition?')) {
      try {
        await deletePromoCodeCondition(params.id_promo_code_condition);
        enqueueSnackbar('Condition deleted successfully');
        setConditions(conditions.filter((row) => row.id_promo_code_condition !== params.id_promo_code_condition));
        if (onConditionUpdate) {
          onConditionUpdate();
        }
      } catch (err) {
        enqueueSnackbar(err.message, { variant: 'error' });
      }
      onConditionRowSelect(null);
    }
  };

  const handleSaveCondition = async (params: PromoCondition) => {
    setRowModesModel({
      ...rowModesModel,
      [params?.id ?? params.id_promo_code_condition]: { mode: GridRowModes.View },
    });
    onConditionRowSelect(null);
  };

  // Function to update config when condition_type changes (only for new rows)
  const updateConditionConfig = (rowId: string, newConditionType: string) => {
    const newConfig = promoCodeConditionTypeConfig.find((ct) => ct.key === newConditionType);
    setConditions((prevConditions) =>
      prevConditions.map((condition) => {
        if (condition.id === rowId) {
          const updatedCondition = { ...condition, config: newConfig };
          // Reset condition_apply_type when condition_type changes for new rows
          const validatedApplyType = getValidatedConditionApplyType(
            newConditionType,
            condition.condition_apply_type,
            promoCodeConditionTypeConfig,
            true, // alwaysReset = true when condition_type changes
          );
          updatedCondition.condition_apply_type =
            validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'];
          return updatedCondition;
        }
        return condition;
      }),
    );
  };

  const processRowUpdate = async (row: PromoCondition) => {
    const rowId = row.id_promo_code_condition ?? row.id;

    // Set loading state
    setLoadingRows((prev) => new Set(prev).add(rowId));

    try {
      // Validate condition_apply_type before save (non-destructive - only fixes invalid values)
      // Since condition_type can't be changed for existing rows, we just validate the apply type
      const validatedApplyType = getValidatedConditionApplyType(
        row.condition_type,
        row.condition_apply_type,
        promoCodeConditionTypeConfig,
        false, // alwaysReset = false for validation before save (non-destructive)
      );

      const payload: operations['createOrUpdatePromoCodeCondition']['requestBody']['content']['application/json'] & {
        promoCodeCondition: { condition_apply_type: string };
      } = {
        promoCodeCondition: {
          id_promo_code_condition: row.id_promo_code_condition,
          condition_type: row.condition_type,
          condition_value:
            typeof row.condition_value === 'string' ? row.condition_value : (row.condition_value as string),
          condition_apply_type:
            validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'],
          id_promo_code: row.id_promo_code,
          customized_message: row.customized_message === '' ? undefined : row.customized_message ?? undefined,
          promo_code_discount_ids: row.promo_code_discount_ids ?? [],
        },
      };

      const result = await createOrUpdatePromoCondition(payload);
      if (result.status !== 200) {
        const error = new Error(JSON.stringify(result.result));
        enqueueSnackbar(JSON.stringify(result.result), {
          autoHideDuration: 5000,
        });
        // Keep the row in edit mode by setting rowModesModel and throwing the error
        setRowModesModel((prevModel) => ({
          ...prevModel,
          [rowId]: { mode: GridRowModes.Edit },
        }));

        // Clear loading state
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });

        throw error;
      } else {
        const newCondition = result.result.promoCodeCondition;
        const updatedCondition = addUniqueID(newCondition, promoCodeConditionTypeConfig);

        // Ensure the returned condition has the validated condition_apply_type
        updatedCondition.condition_apply_type =
          validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'];

        const newConditionArray = conditions.map((c) => {
          if (c.id_promo_code_condition === newCondition.id_promo_code_condition) {
            return updatedCondition;
          } else if (c.id === row.id) {
            return updatedCondition;
          }
          return c;
        });

        setConditions(newConditionArray);
        enqueueSnackbar('Condition saved successfully', { variant: 'success' });

        if (onConditionUpdate) {
          onConditionUpdate();
        }

        // Clear loading state
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });

        return updatedCondition;
      }
    } catch (err) {
      console.error(
        `processConditionRowUpdate - ${row?.id ?? row.id_promo_code_condition} - error\n${err.message}\n${
          err.stack
        }\n\n ${JSON.stringify(row)}`,
      );
      enqueueSnackbar(JSON.stringify(err), {
        autoHideDuration: 5000,
      });
      // Keep the row in edit mode by setting rowModesModel and re-throwing the error
      setRowModesModel((prevModel) => ({
        ...prevModel,
        [rowId]: { mode: GridRowModes.Edit },
      }));

      // Clear loading state
      setLoadingRows((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });

      throw err;
    }
  };

  const handleRowClick = (params: PromoCondition) => {
    onConditionRowSelect(params);
  };

  const sharedOpts: Partial<GridColDef<PromoCondition>> = {
    editable: true,
    sortable: true,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    flex: 1,
  };

  const isNew = (params: PromoCondition | undefined) => {
    if (!params) {
      return false;
    }
    return !('id_promo_code_condition' in params);
  };

  const getColumns = (isCreate = false): Array<GridColDef<PromoCondition>> => [
    {
      ...sharedOpts,
      field: 'condition_type',
      headerName: 'Condition Type',
      type: 'singleSelect',
      valueOptions:
        promoCodeConditionTypeConfig
          .map((ct) => {
            return {
              value: ct.key,
              label: `${ct.isBeingDeprecated ? '~' : ''}${ct.name}${ct.isBeingDeprecated ? '~' : ''}`,
              isBeingDeprecated: ct.isBeingDeprecated,
            };
          })
          .sort((a, b) => {
            if (a.isBeingDeprecated && !b.isBeingDeprecated) {
              return 1;
            } else if (!a.isBeingDeprecated && b.isBeingDeprecated) {
              return -1;
            }
            return 0;
          }) ?? [],
      renderCell: (params) => {
        if (!params.row.config) {
          return <Typography>{params.row.condition_type} (config missing)</Typography>;
        }
        return (
          <Accordion sx={{ width: '100%' }}>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel-content" id="panel-header">
              <Stack spacing={0.5}>
                <Typography>{params.row.config.name}</Typography>
                {!params.row.config.isAvailableInAPP && (
                  <Typography variant="caption" color="warning.main">
                    [Behavior not yet tested via APP]
                  </Typography>
                )}
                {params.row.config.isBeingDeprecated && (
                  <Typography variant="caption" color="warning.main">
                    [This condition type is deprecated and will not be a selectable option soon]
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">{params.row.config.longDescription}</Typography>
            </AccordionDetails>
          </Accordion>
        );
      },
      renderEditCell: (params) => {
        if (!params.row.isNew) {
          return <Typography>{params.row.config.name}</Typography>;
        }

        return (
          <ConditionTypeSelect
            params={params}
            promoCodeConditionTypeConfig={promoCodeConditionTypeConfig}
            onConditionTypeChange={updateConditionConfig}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        // The ConditionTypeSelect component handles the update, but we still need to process the props
        return params.props;
      },
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
      flex: 1,
      minWidth: 150,
    },
    {
      ...sharedOpts,
      field: 'condition_apply_type',
      headerName: 'Condition Apply Type',
      renderCell: (params) => {
        if (params.row.condition_apply_type === 'NONE') {
          return <Typography>--</Typography>;
        }
        return <Typography>{params.row.condition_apply_type}</Typography>;
      },
      renderEditCell: (params) => {
        // Get the latest row data from conditions state to ensure we have the updated config and value
        const currentRow = conditions.find(
          (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
        );
        const row = currentRow || params.row;

        // Try to get config from the row, or load it if missing
        let config = row.config;
        if (!config && row.condition_type) {
          config = promoCodeConditionTypeConfig.find((ct) => ct.key === row.condition_type);
          // Update the row with the config if we found it
          if (config) {
            updateConditionConfig(row.id, row.condition_type);
          }
        }

        const conditionApplyTypes = config?.conditionApplyTypes;
        if (!row.condition_type) {
          return (
            <Tooltip title="Please select a condition type first">
              <Typography>Select condition type first</Typography>
            </Tooltip>
          );
        }

        if (!conditionApplyTypes || row.condition_apply_type == 'NONE') {
          // When there are no conditionApplyTypes, ensure the value is cleared
          return (
            <>
              <Tooltip title="This condition type does not yet support condition apply types, let the marketing team know if you need this functionality for a specific condition type">
                <Typography>--</Typography>
              </Tooltip>
            </>
          );
        }

        // Use a custom Select component with proper DataGrid integration
        return (
          <ConditionApplyTypeSelect
            value={row.condition_apply_type || ''}
            options={conditionApplyTypes}
            id={params.id.toString()}
            field="condition_apply_type"
          />
        );
      },
      preProcessEditCellProps: (params) => {
        // Ensure config is loaded when entering edit mode
        if (!params.row.config && params.row.condition_type) {
          updateConditionConfig(params.row.id, params.row.condition_type);
        }
        // Ensure the value is properly passed to the edit cell and matches the row state
        // This ensures that when condition_type changes and condition_apply_type is reset,
        // the cell value is updated accordingly
        const currentRow = conditions.find(
          (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
        );
        const updatedValue = currentRow?.condition_apply_type || params.row.condition_apply_type || '';
        return {
          ...params.props,
          value: updatedValue,
        };
      },
      align: 'center',
      display: 'flex',
    },

    {
      ...sharedOpts,
      field: 'condition_value',
      headerName: 'Condition Value',
      renderEditCell: (params) => <GridEditInputCell {...params} required />,
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'promo_code_discount_ids',
      renderHeader: () => (
        <HeaderInfo
          title="When discounts are specified here, the discounts will only apply to the order only when the associated condition(s) are met. (when set) Click the condition to highlight which discounts will be only applied with that condition. Multiple conditions can be applied to each discount"
          header="Discount Filter"
          iconType="info"
        />
      ),
      renderCell: (params) =>
        renderMultiDiscountValue(params.row.promo_code_discount_ids ?? [], promoCode.promo_discounts),
      renderEditCell: (params) => {
        if (!promoCodeConditionTypeConfig) {
          return <Spinner />;
        }
        if (!params.row.config?.canUseDiscountFilterIds) {
          return null;
        }
        return (
          <MultiDiscountSelect
            id={`${params.id}`}
            value={params.row.promo_code_discount_ids ?? []}
            promoDiscounts={promoCode.promo_discounts}
            multiSelectType="promo_code_discount_ids"
          />
        );
      },
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'customized_message',
      headerName: 'Customized Msg',
      renderEditCell: (params) => <GridEditInputCell {...params} />,
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'action',
      headerName: 'Action',
      sortable: false,
      editable: false,
      minWidth: 220,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        const row = params.row;
        const rowId = row.id_promo_code_condition ?? row.id;
        const isLoading = loadingRows.has(rowId);

        if (isEditMode) {
          return (
            <Stack spacing={1} alignItems="flex-start" width="100%">
              <Stack direction="row" spacing={1} alignItems="center">
                {isLoading && <CircularProgress size={16} />}
                <Button variant="contained" size="small" onClick={() => handleSaveCondition(row)} disabled={isLoading}>
                  Save
                </Button>
                <Button variant="outlined" size="small" onClick={() => handleCancelCondition(row)} disabled={isLoading}>
                  Cancel
                </Button>
              </Stack>

              <Stack spacing={2}>
                {row.created_at && (
                  <Typography variant="caption" color="textSecondary">
                    Created: <DateFormatterDetailed date={row.created_at} />
                  </Typography>
                )}
                {row.updated_at && (
                  <Typography variant="caption" color="textSecondary">
                    Updated: <DateFormatterDetailed date={row.updated_at} />
                  </Typography>
                )}
              </Stack>

              <PromoCodeUser userId={row.modified_by} />

              {row.id_promo_code_condition && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteCondition(row)}
                  disabled={isLoading}
                >
                  Delete
                </Button>
              )}
            </Stack>
          );
        }

        return (
          <Stack spacing={1} alignItems="flex-start">
            <Button variant="contained" size="small" onClick={() => handleEditCondition(row)} disabled={isLoading}>
              Edit
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'debug',
      headerName: 'Info',
      renderCell: (params) => {
        if (!promoCodeConditionTypeConfig) {
          return null;
        }
        const data = {
          data: params.row,
          config: promoCodeConditionTypeConfig.find((ct) => ct.key === params.row.condition_type),
        };
        return <DebugModal type="generic" data={data ?? { data: 'None' }} />;
      },
      ...sharedOpts,
      flex: 0,
    },
  ];

  return (
    <Box mt={3}>
      <PageSubheader title="Rules">
        <Stack direction="row">
          <Typography variant="h5" pr={2}></Typography>
          {isDev && (
            <DebugModal
              type="generic"
              data={{ allConditions: promoCodeConditionTypeConfig, theseConditions: conditions }}
            />
          )}
          <Button onClick={handleAddCondition} variant="contained" size="small" startIcon={<AddIcon />}>
            Add Condition
          </Button>
        </Stack>
      </PageSubheader>
      {promoCodeConditionTypeConfig ? (
        <DataGrid
          columns={getColumns(isNew(conditions[0]))}
          editMode="row"
          autoHeight
          rows={conditions}
          rowModesModel={rowModesModel}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          pageSizeOptions={DEFAULT_PAGE_SIZES}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => {
            enqueueSnackbar(error.message, { autoHideDuration: 5000 });
          }}
          getRowId={(row) => row.id_promo_code_condition ?? row.id}
          onRowEditStop={noop} // Ignore clicks outside the table
          onRowClick={(params) => handleRowClick(params.row)}
          getRowHeight={() => 'auto'}
          disableColumnSorting
          disableRowSelectionOnClick
          getRowClassName={getRowClassName}
          sx={{
            '& .highlighted-row': {
              backgroundColor: theme.palette.primary.dark,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
              },
            },
          }}
        />
      ) : (
        <Spinner />
      )}
    </Box>
  );
}

export default PromoConditions;
